import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-2 py-12">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-navy-500 mb-6">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
        <p className="text-gray-600 mb-8">Last Updated: January 2025</p>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <FileText className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Agreement to Terms</h2>
                <p className="text-gray-700">
                  By accessing and using qbool, you agree to be bound by these Terms and Conditions. 
                  If you disagree with any part of these terms, you may not access our service.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-4">User Eligibility</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />
                <span>You must be at least 18 years of age</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />
                <span>You must be legally eligible to marry according to the laws of your jurisdiction</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />
                <span>You must use the service with sincere intention to find a spouse</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />
                <span>You must maintain Islamic etiquette in all interactions</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-4">User Responsibilities</h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold mb-2">Profile Information</h3>
                <ul className="space-y-2 ml-4">
                  <li>• Provide accurate and truthful information</li>
                  <li>• Keep your profile updated</li>
                  <li>• Upload appropriate photos that comply with Islamic modesty</li>
                  <li>• Do not create fake or misleading profiles</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Conduct</h3>
                <ul className="space-y-2 ml-4">
                  <li>• Maintain respectful and Islamic conduct</li>
                  <li>• Do not harass or abuse other members</li>
                  <li>• Do not share inappropriate content</li>
                  <li>• Respect privacy and confidentiality</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <AlertTriangle className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Prohibited Activities</h2>
                <p className="text-gray-700 mb-3">The following activities are strictly prohibited:</p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Soliciting money or financial information</li>
                  <li>• Harassment, threats, or abusive behavior</li>
                  <li>• Sharing explicit or inappropriate content</li>
                  <li>• Using the platform for purposes other than marriage</li>
                  <li>• Impersonating another person</li>
                  <li>• Commercial advertising or spam</li>
                  <li>• Automated data collection or scraping</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Premium Memberships</h2>
            <ul className="space-y-2 text-gray-700">
              <li>• Premium features are provided on a subscription basis</li>
              <li>• Payments are processed securely through trusted payment providers</li>
              <li>• Subscriptions auto-renew unless canceled</li>
              <li>• Refunds are provided according to our refund policy</li>
              <li>• Premium features may change with notice</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <Shield className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Limitation of Liability</h2>
                <p className="text-gray-700 mb-3">
                  qbool is a platform to facilitate introductions. We do not:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Guarantee you will find a spouse</li>
                  <li>• Conduct background checks on members</li>
                  <li>• Verify all information provided by users</li>
                  <li>• Take responsibility for interactions between members</li>
                  <li>• Guarantee the accuracy of profiles</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  Users are responsible for their own due diligence and safety.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Account Termination</h2>
            <p className="text-gray-700 mb-3">
              We reserve the right to suspend or terminate accounts that:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Violate these Terms and Conditions</li>
              <li>• Engage in prohibited activities</li>
              <li>• Provide false information</li>
              <li>• Abuse other members</li>
              <li>• Remain inactive for extended periods</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Changes to Terms</h2>
            <p className="text-gray-700">
              We may update these Terms and Conditions from time to time. Continued use of our service 
              after changes constitutes acceptance of the new terms. We will notify users of significant changes.
            </p>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Governing Law</h2>
            <p className="text-gray-700">
              These Terms and Conditions are governed by applicable international laws and regulations. 
              Any disputes will be resolved through appropriate legal channels in your jurisdiction.
            </p>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default TermsPage;